import React, { useMemo } from 'react';
import { Box3, Euler, Vector3 } from 'three';

import { Point3D } from '@paragon/contract-library';
import { convertDegreesToRadians, Direction2D } from '@paragon/geometry-library';
import { Point2D } from '@paragon/three-shared';

import { EditingType } from '../../store/slices/editorSlice';
import { BoundingHitbox } from '../util/BoundingHitBox';
import { R3FSvgVisual } from '../util/R3FSvgVisual';
import { NudgeSettingsButton } from './NudgeSettingsButton';
import { useNudgeSystem } from './useNudgeSystem';

interface Props {
  readonly editType: EditingType;
  readonly leftRight?: boolean;
  readonly topBottom?: boolean;
  readonly moveCallback: (x: number, y: number) => void;
  readonly postEditCallback?: () => void;
  readonly position: Vector3;
  readonly size: number;
  readonly expand?: number;
  readonly coordinateUpDirection: Point2D;
  readonly snappingCoefficient?: number;
  readonly settingsButton?: { type: 'FullTransform' | 'Snapping'; position: Point3D; size?: number };
}

const DEFAULT_EXPANSION_FACTOR = 5 / 11;

const calculateControlGeometry = (size: number, expandFactor = DEFAULT_EXPANSION_FACTOR) => {
  const expansion = size * expandFactor;
  const arrowSize = size / 2;
  const halfSize = arrowSize / 2;
  const [near, far] = [expansion - halfSize, expansion + halfSize];

  const leftBox = new Box3(new Vector3(-far, -halfSize, -0.5), new Vector3(-near, halfSize, 0.5));
  const rightBox = new Box3(new Vector3(near, -halfSize, -0.5), new Vector3(far, halfSize, 0.5));
  const topBox = new Box3(new Vector3(-halfSize, near, -0.5), new Vector3(halfSize, far, 0.5));
  const bottomBox = new Box3(new Vector3(-halfSize, -far, -0.5), new Vector3(halfSize, -near, 0.5));

  const leftArrowPosition = new Vector3().setX(-expansion);
  const rightArrowPosition = new Vector3().setX(expansion);
  const topArrowPosition = new Vector3().setY(expansion);
  const bottomArrowPosition = new Vector3().setY(-expansion);

  return {
    leftBox,
    rightBox,
    topBox,
    bottomBox,
    arrowSize,
    leftArrowPosition,
    rightArrowPosition,
    topArrowPosition,
    bottomArrowPosition,
  };
};

export const NudgeHandle = ({
  editType,
  leftRight,
  topBottom,
  moveCallback,
  postEditCallback,
  position,
  size,
  expand,
  coordinateUpDirection,
  snappingCoefficient,
  settingsButton,
}: Props) => {
  const { startLeft, startRight, startUp, startDown } = useNudgeSystem({
    editType,
    moveCallback,
    postEditCallback,
    coordinateUpDirection,
    snappingCoefficient,
  });

  const zAngleInRadians = useMemo(() => {
    const coordinateUpDirectionClass = Direction2D.fromObject(coordinateUpDirection);
    return coordinateUpDirectionClass.equals(Direction2D.noDirection)
      ? 0
      : convertDegreesToRadians(Direction2D.up.angleTo(coordinateUpDirectionClass));
  }, [coordinateUpDirection]);
  const rotation = useMemo(() => new Euler(0, 0, zAngleInRadians), [zAngleInRadians]);

  const {
    leftBox,
    rightBox,
    topBox,
    bottomBox,
    arrowSize,
    leftArrowPosition,
    rightArrowPosition,
    topArrowPosition,
    bottomArrowPosition,
  } = useMemo(() => calculateControlGeometry(size, expand), [size, expand]);

  const leftRightControls = leftRight ? (
    <>
      <BoundingHitbox box3={leftBox} mouseDown={startLeft} />
      <BoundingHitbox box3={rightBox} mouseDown={startRight} />
      <R3FSvgVisual
        iconName="mui-start-icon"
        rotation={zAngleInRadians + Math.PI}
        size={arrowSize}
        position={leftArrowPosition}
      />
      <R3FSvgVisual
        iconName="mui-start-icon"
        rotation={zAngleInRadians}
        size={arrowSize}
        position={rightArrowPosition}
      />
    </>
  ) : null;

  const topBottomControls = topBottom ? (
    <>
      <BoundingHitbox box3={topBox} mouseDown={startUp} />
      <BoundingHitbox box3={bottomBox} mouseDown={startDown} />
      <R3FSvgVisual
        iconName="mui-start-icon"
        rotation={zAngleInRadians + Math.PI * 0.5}
        size={arrowSize}
        position={topArrowPosition}
      />
      <R3FSvgVisual
        iconName="mui-start-icon"
        rotation={zAngleInRadians + Math.PI * 1.5}
        size={arrowSize}
        position={bottomArrowPosition}
      />
    </>
  ) : null;

  const settingsButtonElement = settingsButton ? (
    <NudgeSettingsButton position={settingsButton.position} size={settingsButton.size} settings={settingsButton.type} />
  ) : null;

  return (
    <group position={position} rotation={rotation}>
      {leftRightControls}
      {topBottomControls}
      {settingsButtonElement}
    </group>
  );
};
